<template>
  <v-app :class="appClasses">
    <v-navigation-drawer v-model="drawer" temporary>
      <v-list>
        <v-list-item @click="$router.push({ name: 'changePassword' })" lines="two">
          <template #prepend>
            <v-avatar><img :src="require('@/assets/service-erp.svg')"/></v-avatar>
          </template>


            <v-list-item-title>{{
                settingsStore.sessionData?.Bediener.Name ?? settingsStore.username
              }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{
                settingsStore.sessionData?.Mandant.find((mandant) => mandant.MandantNr === settingsStore.client)?.Name
              }}
            </v-list-item-subtitle>

        </v-list-item>
      </v-list>

      <v-divider></v-divider>

      <v-list v-model="group" nav>
        <!-- <v-list-item-group
          active-class="text-primary-accent-4"
          mandatory
        > -->
          <template
            v-for="route in routes"
            :key="route.name"
          >
            <v-list-item
              v-if="!route.meta?.hideInNav"
              :value="route.name"
              link :to="route"
            >
              <template #prepend>
                <v-icon color="primary">{{ route.meta?.icon ?? '' }}</v-icon>
              </template>
              <v-list-item-title>{{ route.meta?.title ?? route.name }}</v-list-item-title>
              <template #append v-if="route.meta?.badgeContent">
                <v-badge inline :content="(route.meta?.badgeContent as string | number)" />
              </template>
            </v-list-item>
          </template>
          <v-list-item @click="logout">
            <template #prepend>
              <v-icon color="primary">mdi-logout</v-icon>
            </template>
            <v-list-item-title>
              Abmelden
            </v-list-item-title>
          </v-list-item>
      </v-list>
      <template #append>
        <div class="pa-2 text-center">
          v{{ appVersion }}
        </div>
      </template>
    </v-navigation-drawer>

    <v-app-bar id="mainAppBar" color="primary" theme="dark" :density="isMobile ? 'default' : 'compact'">
      <v-btn
        v-if="canGoBack && $route.matched.length > 1"
        @click.stop="$router.back()"
        icon
        style="z-index: 1;"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-app-bar-nav-icon v-else-if="isAuthenticated" @click.stop="drawer = !drawer"/>

      <v-toolbar-title class="flex-fill">{{ applicationState.title }}</v-toolbar-title>

      <v-btn v-if="$route.meta?.module" icon style="z-index: 1;" @click="showSettingsOverlay = true">
        <v-icon>mdi-cog</v-icon>
      </v-btn>
      <v-btn v-if="$route.meta?.module" icon @click="refreshCurrent">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </v-app-bar>

    <v-main>
      <router-view/>
    </v-main>
    <filter-btn v-if="$route.meta?.filter" fixed-bottom/>
    <v-dialog v-model="showSettingsOverlay" :fullscreen="!isIos">
      <v-card>
        <v-toolbar color="primary" theme="dark">
          <v-toolbar-title>Einstellungen</v-toolbar-title>
          <v-spacer/>
          <v-toolbar-items>
            <v-btn @click="showSettingsOverlay = false" icon style="z-index: 1;">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <Settings @save="showSettingsOverlay = false"/>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import {computed, getCurrentInstance, ref, watch} from 'vue';
import {RouteLocationRaw, RouteRecordRaw} from 'vue-router';
import router from './router';
import {useApplicationState} from './store/applicationState';
import {SplashScreen} from '@capacitor/splash-screen';
import {asyncRef, doAlert, doConfirm} from './util';
import {Device} from '@capacitor/device';
import FilterBtn from './components/FilterBtn.vue';
import Settings from '@/views/Settings.vue';
import {useSettings} from './store/settings';
import {storeMap} from './plugins/piniaStoreById';

const settingsStore = useSettings();
const drawer = ref(false);
const instance = getCurrentInstance();
const appVersion = ref(process.env.VUE_APP_PACKAGE_VERSION || 'dirty');

const routes = computed((): readonly RouteRecordRaw[] => router.options.routes ?? []);
const showSettingsOverlay = ref(false);

const isSlowDevice = asyncRef(true, async () => {
  const info = await Device.getInfo();
  if (info.platform !== 'android') return false;
  const versionParts = info.webViewVersion.split('.');
  const majorVersion = parseInt(versionParts[0]);
  console.log({
    versionParts,
    majorVersion,
  });
  if (majorVersion >= 70) {
    return false;
  }

  return true;
});

const isMobile = asyncRef(true, async () => {
  const info = await Device.getInfo();
  return info.platform == 'android' || info.platform == 'ios';
});

const isIos = asyncRef(true, async () => {
  const info = await Device.getInfo();
  return info.platform == 'ios';
});

const logout = async () => {
  if (!await doConfirm('Wollen Sie sich wirklich abmelden?', 'Wirklich abmelden?', {
    ok: 'Ja',
    cancel: 'Nein',
  })) return;
  await settingsStore.logout();
};
const isAuthenticated = computed(() => {
  return settingsStore.userToken !== null;
});

const route = computed((() => instance?.proxy?.$route));
const group = computed({
  get(): string | null {
    return route.value?.matched[0]?.name as string ?? null;
  },
  set(val: string | null) {
    drawer.value = false;
    router.push({name: val!});
  },
});

const canGoBack = computed(() => {
  return route.value?.meta?.canGoBack as boolean;
});

SplashScreen.hide().catch(console.error);

async function refreshCurrent() {
  try {
    const store = storeMap.get(route.value?.meta?.module as string);
    await store?.fetch();
  } catch (err: any) {
    await doAlert(err.message ?? err, 'Fehler');
  }
}

watch(showSettingsOverlay, (isShown) => {
  if (isShown) return;
  refreshCurrent().catch(console.error);
});

const applicationState = useApplicationState();

function stringify(data: any) {
  return JSON.stringify(data) + ' ';
}

function navigate(to: RouteLocationRaw) {
  router.push(to).catch(console.error);
}

const appClasses = computed(() => {
  if (isSlowDevice.value) {
    return 'no-transistion';
  }

  if (isIos.value) {
    return 'use-safe-area';
  }

  return '';
});

defineExpose({
  drawer,
  canGoBack,
  routes,
  group,
  logout,
  appVersion,
  isAuthenticated,
  isSlowDevice,
  showSettingsOverlay,
  stringify,
  navigate,
  applicationState,
  refreshCurrent,
  settingsStore,
  FilterBtn,
  Settings,
});

</script>

<style lang="scss">
:root {
  --inset-top: env(safe-area-inset-top);
  --inset-bottom: env(safe-area-inset-bottom);
  --inset-left: env(safe-area-inset-left);
  --inset-right: env(safe-area-inset-right);
}

#app-wrapper .no-transitions * {
  transition: none !important;
  transition-duration: 0 !important;
}

.use-safe-area {
  .v-app-bar {
    + .v-main {
      padding-top: calc(56px + var(--inset-top)) !important;
      padding-bottom: var(--inset-bottom) !important;
      @media screen and (min-width: 960px) {
        padding-top: calc(64px + var(--inset-top)) !important;
      }

      > .v-main__wrap {
        > .container, > :not(.container) > .container {
          padding-left: max(var(--inset-left), 12px);
          padding-right: max(var(--inset-right), 12px);
        }
      }
    }
  }

  #mainAppBar {
    padding-top: var(--inset-top);
    height: calc(56px + var(--inset-top)) !important;
    @media screen and (min-width: 960px) {
      height: calc(64px + var(--inset-top)) !important;
    }
  }

  .v-navigation-drawer {
    padding-top: var(--inset-top);
    padding-bottom: var(--inset-bottom);

    > .v-navigation-drawer__content > .v-list {
      padding-left: max(8px, var(--inset-left)) !important;
    }
  }

  .v-dialog__content {
    padding-bottom: var(--inset-bottom) !important;
  }

  .v-footer:last-child {
    padding-bottom: var(--inset-bottom);
  }

  .v-btn--absolute.v-btn--bottom, .v-btn--fixed.v-btn--bottom {
    bottom: max(var(--inset-bottom), 16px);
  }

  .v-btn--absolute.v-btn--right, .v-btn--fixed.v-btn--right {
    right: max(var(--inset-right), 16px);
  }

  .v-btn--absolute.v-btn--left, .v-btn--fixed.v-btn--left {
    left: max(var(--inset-left), 16px);
  }

  .v-btn--absolute.v-btn--top, .v-btn--fixed.v-btn--top {
    top: max(var(--inset-top), 16px);
  }
}
.btn-navigator-primary {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;
}
.footer-fixed {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.flex-1 {
  flex: 1;
}

.spread {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.spread > div {
  width: 100%;
  text-align: center;
}

.spread > div:first-child {
  text-align: left;
}

.spread > div:last-child {
  text-align: right;
}
</style>

<style lang="scss" scoped>
.v-data-table {
  width: 100%;
}
</style>
